"use strict";
ui.loadTemplateFile("knowledgebase.html");

import { IconManager } from "../../js/helpers/iconmanager";
import { TooltipManager } from "../../js/helpers/tooltipmanager";
import { getStoreItems, getStoreTabName } from "../../js/datamanagers/storedatamanager";
import { minimapBoundsToWorld, minimapImagePath, worldToMapImage } from "../../js/maps/mapcoordinates";
import { ITEMTYPE } from "../../js/gamedata/inventorydata";
import {
    KbDrop,
    KbIndex,
    chanceLabel,
    dropSource,
    dropsForItem,
    dropsForMonster,
    monsterIds,
    monsterZones,
    npcZone,
    splitNpcName,
} from "../../js/gamedata/kbdata";

// Same host news.ts reads from.
const INFO_SERVER =
    (typeof config !== "undefined" && config.infoUrl) || "http://localhost:28900";

const ITEM_TYPES: [number, string][] = [
    [ITEMTYPE.WEAPON, "Weapons"],
    [ITEMTYPE.SHIELD, "Sub-weapons"],
    [ITEMTYPE.HELMET, "Headgear"],
    [ITEMTYPE.ARMOR, "Armor"],
    [ITEMTYPE.ARMS, "Gloves"],
    [ITEMTYPE.BOOTS, "Shoes"],
    [ITEMTYPE.BACK, "Back"],
    [ITEMTYPE.FACE_ITEM, "Face"],
    [ITEMTYPE.JEWEL, "Jewelry"],
    [ITEMTYPE.USE, "Consumables"],
    [ITEMTYPE.GEM, "Gems & etc."],
    [ITEMTYPE.NATURAL, "Materials"],
    [ITEMTYPE.QUEST, "Quest items"],
    [ITEMTYPE.RIDE_PART, "Cart parts"],
];
const ITEM_TYPE_NAME: Record<number, string> = Object.fromEntries(ITEM_TYPES);

const SOURCE_LABEL = { mob: "Monster drop", zone: "Zone drop", level: "Level range", global: "Any kill" };
const DROP_GROUPS: [keyof typeof SOURCE_LABEL, string][] = [
    ["mob", "Own drops"],
    ["zone", "Zone drops"],
    ["level", "Level-range drops"],
    ["global", "Any kill"],
];
const DROP_NOTE =
    "Each kill rolls every entry at the chance shown, then drops one of the entries that hit.";

// LIST_NPC.STB store tab columns, see npcstats.ts getStoreTabs.
const STORE_TAB_COLUMNS = [21, 22, 23, 24];

interface Marker {
    x: number;
    y: number;
    kind: "mob" | "npc" | "warp" | "you";
    label: string;
    nav?: string;
}

interface SearchEntry {
    label: string;
    lower: string;
    kind: "zone" | "mob" | "npc" | "item";
    nav: string;
    item?: string;
}

const esc = (value: any) =>
    String(value).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

class KnowledgeBaseDialog extends Dialog {
    _element: any;
    _main: any;
    _index: KbIndex | null = null;
    _loading: Promise<void> | null = null;
    _spawns: Record<number, Promise<{ x: number; y: number; mobs: number[] }[]>> = {};
    _history: string[] = [];
    _pos = -1;
    _recent: { nav: string; html: string }[] = [];
    _renderToken = 0;
    _maps: { id: string; map: number; markers: Promise<Marker[]> | Marker[] }[] = [];
    _query = "";
    _names = new Map<string, string>();
    _itemsByType = new Map<number, { no: number; name: string }[]>();
    _search: SearchEntry[] | null = null;
    _soldBy: Map<string, number[]> | null = null;

    constructor() {
        super("knowledgebase.html");
        this._main = this._element.find(".kb-main");

        this._element.on("click", "[data-nav]", (e: any) => {
            e.preventDefault();
            this.navigate($(e.currentTarget).attr("data-nav"));
        });
        this._element.find(".kb-back").on("click", () => this._go(-1));
        this._element.find(".kb-forward").on("click", () => this._go(1));

        let searchTimer: any;
        this._element.find(".kb-search").on("input", (e: any) => {
            this._query = String(e.target.value).trim();
            clearTimeout(searchTimer);
            searchTimer = setTimeout(() => this.navigate(this._query ? "search" : "home"), 150);
        });

        this._element
            .on("mouseenter", "[data-item]", (e: any) => {
                const [itemType, itemNo] = $(e.currentTarget).attr("data-item").split(":").map(Number);
                TooltipManager.showTooltip(TooltipManager.getItemTooltip({ itemType, itemNo }), e.pageX, e.pageY);
            })
            .on("mousemove", "[data-item]", (e: any) => TooltipManager.moveTooltip(e.pageX, e.pageY))
            .on("mouseleave", "[data-item]", () => TooltipManager.hideTooltip());

        this.center();
    }

    // Nothing loads until the dialog is first opened.
    show() {
        super.show();
        this._open();
    }

    toggle() {
        super.toggle();
        this._open();
    }

    _open() {
        if (this._pos < 0 && this._element.is(":visible")) this.navigate("home");
    }

    navigate(nav: string) {
        const current = this._history[this._pos];
        if (nav !== current) {
            // Typing refines the current search instead of stacking entries.
            if (nav === "search" && current === "search") return this.render();
            this._history = this._history.slice(0, this._pos + 1);
            this._history.push(nav);
            this._pos = this._history.length - 1;
        }
        this.render();
    }

    _go(step: number) {
        const pos = this._pos + step;
        if (pos < 0 || pos >= this._history.length) return;
        this._pos = pos;
        this.render();
    }

    _ready(): Promise<void> {
        if (!this._loading) {
            const tables = new Promise<void>((resolve) =>
                GDM.get("item_data", "list_npc", "npc_names", "list_zone", "zone_names", "list_sell", () => resolve())
            );
            const index = fetch(`${INFO_SERVER}/api/kb`)
                .then((res) => {
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    return res.json();
                })
                .then((data) => {
                    this._index = data;
                });
            this._loading = Promise.all([tables, index]).then(() => undefined);
            this._loading.catch(() => (this._loading = null));
        }
        return this._loading;
    }

    async render() {
        const nav = this._history[this._pos];
        const token = ++this._renderToken;
        const [page, a, b] = nav.split(":");

        this._element.find(".kb-back").prop("disabled", this._pos <= 0);
        this._element.find(".kb-forward").prop("disabled", this._pos >= this._history.length - 1);
        const section = { zone: "zones", monster: "monsters", npc: "npcs", item: "items" }[page] || page;
        this._element.find(".kb-nav-link").removeClass("active");
        this._element.find(`.kb-nav-link[data-nav="${section}"]`).addClass("active");
        if (page !== "search") this._element.find(".kb-search").val("");

        if (!this._index) this._main.html('<div class="kb-status">Loading...</div>');
        try {
            await this._ready();
        } catch (err) {
            if (token === this._renderToken) {
                this._main.html(
                    `<div class="kb-status kb-error">Could not load the knowledge base from the info server (${esc(err)}).` +
                        ` <a class="kb-link" data-nav="${esc(nav)}">Retry</a></div>`
                );
            }
            return;
        }
        if (token !== this._renderToken) return;

        this._maps = [];
        const n = Number(a);
        const pages: Record<string, () => string> = {
            home: () => this._homePage(),
            search: () => this._searchPage(),
            zones: () => this._zonesPage(),
            zone: () => this._zonePage(n, b || "mobs"),
            monsters: () => this._monstersPage(),
            monster: () => this._monsterPage(n),
            npcs: () => this._npcsPage(),
            npc: () => this._npcPage(n),
            items: () => this._itemsPage(a ? n : ITEMTYPE.NATURAL),
            item: () => this._itemPage(n, Number(b)),
        };
        this._main.html((pages[page] || pages.home)()).scrollTop(0);
        for (const spec of this._maps) this._setupMap(spec, token);

        if (["zone", "monster", "npc", "item"].includes(page)) {
            this._remember([page, a, page === "item" ? b : undefined].filter((x) => x !== undefined).join(":"));
        }
    }

    // ---- names & links -------------------------------------------------

    _memo(key: string, fn: () => string): string {
        let value = this._names.get(key);
        if (value === undefined) {
            try {
                value = fn() || "";
            } catch (err) {
                value = "";
            }
            this._names.set(key, value);
        }
        return value;
    }

    _npcName(id: number): string {
        return (
            this._memo(`n${id}`, () => {
                const row = GDM.getNow("list_npc").row(id);
                return GDM.getNow("npc_names").getByKey(row[40])?.text || row[0];
            }) || `#${id}`
        );
    }

    _zoneName(map: number): string {
        return (
            this._memo(`z${map}`, () =>
                GDM.getNow("zone_names").getByKey(GDM.getNow("list_zone").item(map, 26))?.text
            ) || `Zone ${map}`
        );
    }

    _itemName(type: number, no: number): string {
        return this._memo(`i${type}:${no}`, () => GDM.getNow("item_data").getName(type, no));
    }

    _zoneRow(map: number): string[] | null {
        try {
            return GDM.getNow("list_zone").row(map);
        } catch (err) {
            return null;
        }
    }

    _mobLink(id: number) {
        return `<a class="kb-link kb-mob" data-nav="monster:${id}">${esc(this._npcName(id))}</a>`;
    }

    _npcLink(type: number) {
        return `<a class="kb-link kb-npc" data-nav="npc:${type}">${esc(this._npcName(type))}</a>`;
    }

    _zoneLink(map: number) {
        return `<a class="kb-link kb-zone" data-nav="zone:${map}">${esc(this._zoneName(map))}</a>`;
    }

    _itemLink(type: number, no: number) {
        const name = this._itemName(type, no) || `Item ${type}:${no}`;
        return `<a class="kb-link kb-item" data-nav="item:${type}:${no}" data-item="${type}:${no}">${esc(name)}</a>`;
    }

    _icon(type: number, no: number, big = false) {
        let icon = "";
        try {
            icon = IconManager.getItemIcon({ itemType: type, itemNo: no, quantity: 1 })[0].outerHTML;
        } catch (err) {
            // Row missing from the client's STB: show an empty slot.
        }
        return `<a class="kb-icon${big ? " kb-icon-big" : ""}" data-nav="item:${type}:${no}" data-item="${type}:${no}">${icon}</a>`;
    }

    _level(mob: number): number | null {
        return this._index!.monsters[mob]?.level ?? null;
    }

    _remember(nav: string) {
        const [page, a, b] = nav.split(":").map((x, i) => (i ? Number(x) : x)) as [string, number, number];
        const html = {
            zone: () => this._zoneLink(a),
            monster: () => this._mobLink(a),
            npc: () => this._npcLink(a),
            item: () => this._itemLink(a, b),
        }[page]!();
        this._recent = [{ nav, html }, ...this._recent.filter((r) => r.nav !== nav)].slice(0, 8);
        this._element.find(".kb-recent").html(this._recent.map((r) => r.html).join(""));
    }

    // ---- shared pieces -------------------------------------------------

    _crumbs(parts: [string, string?][]) {
        return (
            '<div class="kb-crumbs">' +
            parts
                .map(([label, nav]) => (nav ? `<a data-nav="${nav}">${esc(label)}</a>` : `<span>${esc(label)}</span>`))
                .join('<span class="kb-sep">&rsaquo;</span>') +
            "</div>"
        );
    }

    _tag(kind: string, label: string, extra = "") {
        return `<div class="kb-tagline"><span class="kb-tag kb-${kind}">${esc(label)}</span>${extra ? `<span>${esc(extra)}</span>` : ""}</div>`;
    }

    _facts(facts: [string, any][]) {
        return (
            '<div class="kb-facts">' +
            facts.map(([k, v]) => `<div><div class="kb-k">${esc(k)}</div><div class="kb-v">${v}</div></div>`).join("") +
            "</div>"
        );
    }

    _table(headers: string[], rows: string[][], empty: string) {
        if (!rows.length) return `<div class="kb-empty">${esc(empty)}</div>`;
        return (
            '<table class="kb-table"><thead><tr>' +
            headers.map((h) => `<th>${esc(h)}</th>`).join("") +
            "</tr></thead><tbody>" +
            rows.map((r) => "<tr>" + r.map((c) => `<td>${c}</td>`).join("") + "</tr>").join("") +
            "</tbody></table>"
        );
    }

    _chance(prob: number) {
        const width = Math.min(100, prob / 10);
        return `<span class="kb-chance"><span class="kb-bar"><span style="width:${width}%"></span></span>${chanceLabel(prob)}</span>`;
    }

    _map(map: number, markers: Promise<Marker[]> | Marker[], cls = "") {
        const id = `kb-map-${this._maps.length}`;
        this._maps.push({ id, map, markers });
        return `<div class="kb-map ${cls}" id="${id}"><img alt=""><div class="kb-markers"></div></div>`;
    }

    _setupMap(spec: { id: string; map: number; markers: Promise<Marker[]> | Marker[] }, token: number) {
        const el = this._main.find(`#${spec.id}`);
        const row = this._zoneRow(spec.map);
        const noImage = () => el.addClass("kb-map-empty").html("<span>No map image</span>");
        if (!row || !row[8]) return noImage();

        const img = el.find("img");
        img.on("error", noImage).on("load", async () => {
            const w = img[0].naturalWidth;
            const h = img[0].naturalHeight;
            const bounds = minimapBoundsToWorld(Number(row[9]), Number(row[10]), w, h);
            const markers = await spec.markers;
            if (token !== this._renderToken) return;
            el.find(".kb-markers").html(
                markers
                    .map((m) => {
                        const p = worldToMapImage(m.x, m.y, bounds, w, h);
                        if (!p.inside) return "";
                        const nav = m.nav ? ` data-nav="${m.nav}"` : "";
                        return `<a class="kb-marker kb-marker-${m.kind}" style="left:${(p.x / w) * 100}%;top:${(p.y / h) * 100}%" title="${esc(m.label)}"${nav}></a>`;
                    })
                    .join("")
            );
        });
        img.attr("src", minimapImagePath(row[8]));
    }

    _spawnsFor(map: number) {
        if (!this._spawns[map]) {
            this._spawns[map] = fetch(`${INFO_SERVER}/api/kb/zones/${map}/spawns`)
                .then((res) => (res.ok ? res.json() : []))
                .catch(() => []);
        }
        return this._spawns[map];
    }

    _spawnMarkers(map: number, only?: number): Promise<Marker[]> {
        return this._spawnsFor(map).then((spawns) =>
            spawns
                .filter((s) => only === undefined || s.mobs.includes(only))
                .map((s) => ({
                    x: s.x,
                    y: s.y,
                    kind: "mob" as const,
                    label: s.mobs.map((m) => this._npcName(m)).join(", "),
                    nav: s.mobs.length === 1 || only !== undefined ? `monster:${only ?? s.mobs[0]}` : undefined,
                }))
        );
    }

    // Zones without spawns, NPCs or warps aren't in the index.
    _zone(map: number) {
        return this._index!.zones[map] || { mobs: {}, npcs: [], warps: [] };
    }

    _zoneMarkers(map: number): Marker[] {
        const zone = this._zone(map);
        const markers: Marker[] = [
            ...zone.npcs.map((n) => ({ x: n.x, y: n.y, kind: "npc" as const, label: this._npcName(n.type), nav: `npc:${n.type}` })),
            ...zone.warps.map((w) => ({
                x: w.x,
                y: w.y,
                kind: "warp" as const,
                label: `To ${this._zoneName(w.destMap)}`,
                nav: `zone:${w.destMap}`,
            })),
        ];
        const MC = (window as any).MC;
        if (MC && MC.zoneNo === map && MC.position) {
            markers.push({ x: MC.position.x, y: MC.position.y, kind: "you", label: "You are here" });
        }
        return markers;
    }

    _zoneMobs(map: number): number[] {
        return Object.keys(this._zone(map).mobs)
            .map(Number)
            .sort((x, y) => (this._level(x) ?? 0) - (this._level(y) ?? 0));
    }

    _levelRange(mobs: number[]): string {
        const levels = mobs.map((m) => this._level(m)).filter((l): l is number => l !== null);
        if (!levels.length) return "—";
        const lo = Math.min(...levels);
        const hi = Math.max(...levels);
        return lo === hi ? `${lo}` : `${lo} – ${hi}`;
    }

    _npcTypes(): { type: number; map: number }[] {
        return Object.entries(this._index!.zones).flatMap(([map, zone]) =>
            zone.npcs.map((n) => ({ type: n.type, map: Number(map) }))
        );
    }

    _storeTabs(type: number): { name: string; items: { itemType: number; itemNo: number }[] }[] {
        try {
            const row = GDM.getNow("list_npc").row(type);
            return STORE_TAB_COLUMNS.map((col) => Number(row[col]) || 0)
                .filter((sellId) => sellId)
                .map((sellId) => ({ name: getStoreTabName(sellId), items: getStoreItems(sellId) }));
        } catch (err) {
            return [];
        }
    }

    _sellers(type: number, no: number): number[] {
        if (!this._soldBy) {
            this._soldBy = new Map();
            for (const { type: npc } of this._npcTypes()) {
                for (const tab of this._storeTabs(npc)) {
                    for (const item of tab.items) {
                        const key = `${item.itemType}:${item.itemNo}`;
                        const list = this._soldBy.get(key) || [];
                        if (!list.includes(npc)) list.push(npc);
                        this._soldBy.set(key, list);
                    }
                }
            }
        }
        return this._soldBy.get(`${type}:${no}`) || [];
    }

    _itemsOfType(type: number): { no: number; name: string }[] {
        let items = this._itemsByType.get(type);
        if (!items) {
            items = [];
            const table = GDM.getNow("item_data").data[type];
            for (let no = 1; table && no < table.rowCount; no++) {
                const name = this._itemName(type, no);
                if (name) items.push({ no, name });
            }
            this._itemsByType.set(type, items);
        }
        return items;
    }

    // ---- pages ---------------------------------------------------------

    _homePage() {
        const index = this._index!;
        const MC = (window as any).MC;
        const here = MC && this._zoneRow(MC.zoneNo)?.[1] ? MC.zoneNo : null;
        const npcCount = this._npcTypes().length;

        let html = "";
        if (here !== null) {
            const mobs = this._zoneMobs(here);
            html += `
                <section class="kb-here">
                    ${this._map(here, this._zoneMarkers(here), "kb-map-thumb")}
                    <div class="kb-here-text">
                        <div class="kb-k">You are here</div>
                        <h1>${this._zoneLink(here)}</h1>
                        <div class="kb-muted">${mobs.length ? "Monsters in this zone" : "No monsters spawn here"}</div>
                        <div class="kb-chips">${mobs
                            .map((m) => `<a class="kb-chip kb-mob" data-nav="monster:${m}">${esc(this._npcName(m))}<span>Lv ${this._level(m) ?? "?"}</span></a>`)
                            .join("")}</div>
                    </div>
                </section>`;
        }

        const tiles: [string, string, string, string][] = [
            ["zones", "zone", "Zones", `${Object.keys(index.zones).length} zones · spawns, NPCs and warps`],
            ["monsters", "mob", "Monsters", `${monsterIds(index).length} monsters · stats, spawns, drops`],
            ["npcs", "npc", "NPCs", `${npcCount} NPCs · locations and shops`],
            ["items", "item", "Items", "Where to get them"],
        ];
        html += `
            <section><h2>Browse</h2><div class="kb-tiles">${tiles
                .map(([nav, kind, title, desc]) => `<a class="kb-tile kb-${kind}" data-nav="${nav}"><span class="kb-tile-title">${title}</span><span>${esc(desc)}</span></a>`)
                .join("")}</div></section>
            <section><h2>Item categories</h2><div class="kb-chips">${ITEM_TYPES.map(
                ([type, label]) => `<a class="kb-chip kb-item" data-nav="items:${type}">${label}</a>`
            ).join("")}</div></section>`;
        return html;
    }

    _searchPage() {
        const index = this._index!;
        if (!this._search) {
            const entries: SearchEntry[] = [];
            const add = (label: string, kind: SearchEntry["kind"], nav: string, item?: string) =>
                label && entries.push({ label, lower: label.toLowerCase(), kind, nav, item });
            for (const map of Object.keys(index.zones).map(Number)) add(this._zoneName(map), "zone", `zone:${map}`);
            for (const mob of monsterIds(index)) add(this._npcName(mob), "mob", `monster:${mob}`);
            for (const { type } of this._npcTypes()) add(this._npcName(type), "npc", `npc:${type}`);
            for (const [type] of ITEM_TYPES) {
                for (const { no, name } of this._itemsOfType(type)) add(name, "item", `item:${type}:${no}`, `${type}:${no}`);
            }
            this._search = entries;
        }

        const q = this._query.toLowerCase();
        // Name-prefix matches first, then items you can actually get, then A-Z.
        const dropped = new Set(index.drops.map((d) => `${d.type}:${d.id}`));
        const rank = (e: SearchEntry) => {
            const obtainable = !e.item || dropped.has(e.item) || this._sellers(...(e.item.split(":").map(Number) as [number, number])).length > 0;
            return (e.lower.startsWith(q) ? 0 : 2) + (obtainable ? 0 : 1);
        };
        const hits = this._search
            .filter((e) => e.lower.includes(q))
            .map((e) => ({ e, r: rank(e) }))
            .sort((x, y) => x.r - y.r || x.e.label.localeCompare(y.e.label))
            .map((x) => x.e);
        const groups: [SearchEntry["kind"], string][] = [["zone", "Zones"], ["mob", "Monsters"], ["npc", "NPCs"], ["item", "Items"]];
        const body = groups
            .map(([kind, title]) => {
                const list = hits.filter((h) => h.kind === kind);
                if (!list.length) return "";
                const shown = list.slice(0, 40);
                return `<section><h2>${title} <span class="kb-count">${list.length}</span></h2><div class="kb-results">${shown
                    .map((h) =>
                        kind === "item"
                            ? `<div class="kb-result">${this._icon(...(h.item!.split(":").map(Number) as [number, number]))}<a class="kb-link kb-item" data-nav="${h.nav}" data-item="${h.item}">${esc(h.label)}</a></div>`
                            : `<div class="kb-result"><a class="kb-link kb-${kind}" data-nav="${h.nav}">${esc(h.label)}</a></div>`
                    )
                    .join("")}</div>${list.length > shown.length ? `<div class="kb-muted">${list.length - shown.length} more, refine the search</div>` : ""}</section>`;
            })
            .join("");
        return `${this._crumbs([["Home", "home"], [`Search: ${this._query}`]])}${body || '<div class="kb-empty">Nothing found.</div>'}`;
    }

    _zonesPage() {
        const maps = Object.keys(this._index!.zones)
            .map(Number)
            .filter((map) => this._zoneRow(map)?.[1]);
        const minLevel = (map: number) => this._level(this._zoneMobs(map)[0]) ?? 999;
        maps.sort((x, y) => minLevel(x) - minLevel(y));
        return `
            ${this._crumbs([["Home", "home"], ["Zones"]])}
            <h1>Zones</h1>
            <div class="kb-cards">${maps
                .map((map) => {
                    const zone = this._index!.zones[map];
                    const mobs = this._zoneMobs(map);
                    return `<a class="kb-card" data-nav="zone:${map}"><span class="kb-link kb-zone">${esc(this._zoneName(map))}</span>
                        <span class="kb-muted">${mobs.length ? `Lv ${this._levelRange(mobs)} · ${mobs.length} monsters` : "No monsters"} · ${zone.npcs.length} NPCs</span></a>`;
                })
                .join("")}</div>`;
    }

    _zonePage(map: number, tab: string) {
        const zone = this._zone(map);
        if (!this._zoneRow(map)?.[1]) return `${this._crumbs([["Home", "home"], ["Zones", "zones"]])}<div class="kb-empty">Unknown zone.</div>`;
        const mobs = this._zoneMobs(map);
        const zoneDrops = this._index!.drops.filter((d) => d.map === map).sort((x, y) => y.prob - x.prob);
        const connected = [...new Set(zone.warps.map((w) => w.destMap))];
        const markers = Promise.all([this._spawnMarkers(map), this._zoneMarkers(map)]).then(([s, z]) => [...s, ...z]);
        const tabs: [string, string][] = [
            ["mobs", `Monsters (${mobs.length})`],
            ["npcs", `NPCs (${zone.npcs.length})`],
            ["drops", `Zone drops (${zoneDrops.length})`],
        ];

        let body = "";
        if (tab === "npcs") {
            body = this._table(
                ["NPC", "Role", "Position"],
                zone.npcs.map((n) => [this._npcLink(n.type), esc(splitNpcName(this._npcName(n.type)).role || "—"), `${n.x}, ${n.y}`]),
                "No NPCs in this zone."
            );
        } else if (tab === "drops") {
            body =
                '<p class="kb-muted">Items any monster killed in this zone can drop.</p>' +
                this._table(
                    ["Item", "Type", "Chance"],
                    zoneDrops.map((d) => [
                        `<span class="kb-inline">${this._icon(d.type, d.id)}${this._itemLink(d.type, d.id)}</span>`,
                        esc(ITEM_TYPE_NAME[d.type] || ""),
                        this._chance(d.prob),
                    ]),
                    "No zone-wide drops."
                );
        } else {
            body = this._table(
                ["Monster", "Level", "Spawn groups", "Best own drop"],
                mobs.map((m) => {
                    const own = this._index!.drops.filter((d) => d.mob === m).sort((x, y) => y.prob - x.prob)[0];
                    return [this._mobLink(m), `${this._level(m) ?? "—"}`, `${zone.mobs[m]}`, own ? this._itemLink(own.type, own.id) : "—"];
                }),
                "No monsters spawn here."
            );
        }

        return `
            ${this._crumbs([["Home", "home"], ["Zones", "zones"], [this._zoneName(map)]])}
            <section class="kb-hero">
                <figure>
                    ${this._map(map, markers)}
                    <figcaption class="kb-legend">
                        <span><i class="kb-marker-mob"></i>Spawn</span><span><i class="kb-marker-npc"></i>NPC</span>
                        <span><i class="kb-marker-warp"></i>Warp</span><span><i class="kb-marker-you"></i>You</span>
                    </figcaption>
                </figure>
                <div class="kb-hero-text">
                    ${this._tag("zone", "Zone", `#${map}`)}
                    <h1>${esc(this._zoneName(map))}</h1>
                    ${this._facts([
                        ["Monster levels", this._levelRange(mobs)],
                        ["Monsters", mobs.length],
                        ["NPCs", zone.npcs.length],
                        ["Spawn groups", Object.values(zone.mobs).reduce((s, c) => s + c, 0)],
                    ])}
                    ${connected.length ? `<div class="kb-k">Connected zones</div><div class="kb-chips">${connected
                        .map((m) => `<a class="kb-chip kb-zone" data-nav="zone:${m}">${esc(this._zoneName(m))}</a>`)
                        .join("")}</div>` : ""}
                </div>
            </section>
            <div class="kb-tabs">${tabs
                .map(([id, label]) => `<button class="kb-tab${id === tab ? " active" : ""}" data-nav="zone:${map}:${id}">${label}</button>`)
                .join("")}</div>
            ${body}`;
    }

    _monstersPage() {
        const mobs = monsterIds(this._index!).sort((x, y) => (this._level(x) ?? 0) - (this._level(y) ?? 0));
        return `
            ${this._crumbs([["Home", "home"], ["Monsters"]])}
            <h1>Monsters</h1>
            ${this._table(
                ["Monster", "Level", "HP", "Found in"],
                mobs.map((m) => [
                    this._mobLink(m),
                    `${this._level(m) ?? "—"}`,
                    `${this._index!.monsters[m]?.hp ?? "—"}`,
                    monsterZones(this._index!, m).slice(0, 3).map((z) => this._zoneLink(z.map)).join(", "),
                ]),
                "No monsters."
            )}`;
    }

    _monsterPage(id: number) {
        const index = this._index!;
        const stats = index.monsters[id];
        const zones = monsterZones(index, id);
        const drops = dropsForMonster(index, id);
        const name = this._npcName(id);

        const dropGroups = DROP_GROUPS.map(([source, title]) => {
            const list = drops.filter((d) => dropSource(d) === source);
            if (!list.length) return "";
            return `<div class="kb-k">${title}</div>${list
                .map((d) => `<div class="kb-drop">${this._icon(d.type, d.id)}<span class="kb-drop-name">${this._itemLink(d.type, d.id)}<span class="kb-muted">${esc(ITEM_TYPE_NAME[d.type] || "")}${d.map ? ` · ${esc(this._zoneName(d.map))}` : ""}</span></span>${this._chance(d.prob)}</div>`)
                .join("")}`;
        }).join("");

        const v = (value: number | undefined) => (value === undefined ? "—" : String(value));
        return `
            ${this._crumbs([["Home", "home"], ["Monsters", "monsters"], [name]])}
            <section class="kb-hero">
                <figure>
                    ${zones.length ? this._map(zones[0].map, this._spawnMarkers(zones[0].map, id)) : '<div class="kb-map kb-map-empty"><span>Does not spawn</span></div>'}
                    ${zones.length ? `<figcaption class="kb-muted">Spawn groups in ${esc(this._zoneName(zones[0].map))}</figcaption>` : ""}
                </figure>
                <div class="kb-hero-text">
                    ${this._tag("mob", "Monster", stats ? (stats.aggressive ? "Aggressive" : "Passive") : "")}
                    <h1>${esc(name)} <small>Lv ${v(stats?.level)}</small></h1>
                    ${this._facts([
                        ["HP", v(stats?.hp)],
                        ["Attack", v(stats?.attack)],
                        ["Defense", v(stats?.defense)],
                        ["EXP", v(stats?.exp)],
                        ["Accuracy", v(stats?.accuracy)],
                        ["Dodge", v(stats?.dodge)],
                        ["Move speed", v(stats?.speed)],
                    ])}
                </div>
            </section>
            <section class="kb-columns">
                <div>
                    <h2>Found in</h2>
                    ${zones.length ? zones.map((z) => `<div class="kb-row">${this._zoneLink(z.map)}<span class="kb-muted">${z.groups} spawn groups</span></div>`).join("") : '<div class="kb-empty">Not found in any zone.</div>'}
                </div>
                <div>
                    <h2>Drops</h2>
                    ${dropGroups ? `${dropGroups}<p class="kb-muted">${DROP_NOTE}</p>` : '<div class="kb-empty">Drops nothing.</div>'}
                </div>
            </section>`;
    }

    _npcsPage() {
        const byZone = Object.entries(this._index!.zones).filter(([, zone]) => zone.npcs.length);
        return `
            ${this._crumbs([["Home", "home"], ["NPCs"]])}
            <h1>NPCs</h1>
            ${byZone
                .map(([map, zone]) => `<section><h2>${this._zoneLink(Number(map))}</h2><div class="kb-results">${zone.npcs
                    .map((n) => `<div class="kb-result">${this._npcLink(n.type)}</div>`)
                    .join("")}</div></section>`)
                .join("")}`;
    }

    _npcPage(type: number) {
        const loc = npcZone(this._index!, type);
        const { role, name } = splitNpcName(this._npcName(type));
        const tabs = this._storeTabs(type);
        const others = loc ? this._index!.zones[loc.map].npcs.filter((n) => n.type !== type) : [];
        const marker: Marker[] = loc ? [{ x: loc.x, y: loc.y, kind: "npc", label: name }] : [];

        return `
            ${this._crumbs([["Home", "home"], ["NPCs", "npcs"], [name]])}
            <section class="kb-hero">
                <figure>${loc ? this._map(loc.map, marker) : '<div class="kb-map kb-map-empty"><span>Location unknown</span></div>'}</figure>
                <div class="kb-hero-text">
                    ${this._tag("npc", "NPC", role)}
                    <h1>${esc(name)}</h1>
                    ${loc ? this._facts([["Location", this._zoneLink(loc.map)], ["Position", `${loc.x}, ${loc.y}`]]) : ""}
                </div>
            </section>
            <section class="kb-columns">
                <div>
                    <h2>Sells</h2>
                    ${tabs.length ? tabs.map((t) => `<div class="kb-k">${esc(t.name)}</div><div class="kb-slots">${t.items.map((i) => this._icon(i.itemType, i.itemNo)).join("")}</div>`).join("") : '<div class="kb-empty">No shop.</div>'}
                </div>
                <div>
                    <h2>Also in this zone</h2>
                    ${others.length ? others.map((n) => `<div class="kb-row">${this._npcLink(n.type)}</div>`).join("") : '<div class="kb-empty">No other NPCs.</div>'}
                </div>
            </section>`;
    }

    _itemsPage(type: number) {
        const counts = new Map<number, number>();
        for (const d of this._index!.drops) if (d.type === type) counts.set(d.id, (counts.get(d.id) || 0) + 1);
        const items = this._itemsOfType(type);
        return `
            ${this._crumbs([["Home", "home"], ["Items", "items"], [ITEM_TYPE_NAME[type] || "Items"]])}
            <div class="kb-chips">${ITEM_TYPES.map(
                ([t, label]) => `<a class="kb-chip kb-item${t === type ? " active" : ""}" data-nav="items:${t}">${label}</a>`
            ).join("")}</div>
            <div class="kb-item-grid">${items
                .map(({ no }) => {
                    const drops = counts.get(no) || 0;
                    const sold = this._sellers(type, no).length;
                    const badges = [drops ? `${drops} drop${drops > 1 ? "s" : ""}` : "", sold ? "shop" : ""].filter(Boolean).join(" · ");
                    return `<div class="kb-drop">${this._icon(type, no)}<span class="kb-drop-name">${this._itemLink(type, no)}<span class="kb-muted">${badges}</span></span></div>`;
                })
                .join("")}</div>`;
    }

    _itemPage(type: number, no: number) {
        const index = this._index!;
        const name = this._itemName(type, no) || `Item ${type}:${no}`;
        let description = "";
        let row: string[] = [];
        try {
            description = GDM.getNow("item_data").getDescription(type, no) || "";
            row = GDM.getNow("item_data").getData(type, no);
        } catch (err) {
            // Unknown item row: header only.
        }

        const dropRows = dropsForItem(index, type, no).map((d: KbDrop) => {
            const source = dropSource(d);
            let who = "Any monster";
            let level = "—";
            let where = d.map ? this._zoneLink(d.map) : "All zones";
            if (source === "mob") {
                who = this._mobLink(d.mob);
                level = `${this._level(d.mob) ?? "—"}`;
                if (!d.map) where = monsterZones(index, d.mob).slice(0, 3).map((z) => this._zoneLink(z.map)).join(", ") || "—";
            } else if (source === "level") {
                who = `Any monster Lv ${d.minLevel}–${d.maxLevel}`;
                level = `${d.minLevel}–${d.maxLevel}`;
            }
            return [who, level, where, `<span class="kb-source">${SOURCE_LABEL[source]}</span>`, this._chance(d.prob)];
        });

        const sellers = this._sellers(type, no);
        const num = (col: number) => Number(row[col]) || 0;

        return `
            ${this._crumbs([["Home", "home"], ["Items", "items"], [ITEM_TYPE_NAME[type] || "Items", `items:${type}`], [name]])}
            <section class="kb-hero kb-hero-item">
                ${this._icon(type, no, true)}
                <div class="kb-hero-text">
                    ${this._tag("item", ITEM_TYPE_NAME[type] || "Item", `#${no}`)}
                    <h1>${esc(name)}</h1>
                    ${description ? `<p class="kb-desc">${esc(description)}</p>` : ""}
                    ${this._facts([["Base price", num(5)], ["Weight", num(7)], ["Quality", num(8)]])}
                </div>
            </section>
            <section class="kb-columns kb-columns-wide">
                <div>
                    <h2>Dropped by</h2>
                    ${this._table(["Source", "Lv", "Zone", "Type", "Chance"], dropRows, "Not dropped by monsters.")}
                    ${dropRows.length ? `<p class="kb-muted">${DROP_NOTE}</p>` : ""}
                </div>
                <div>
                    <h2>Sold by</h2>
                    ${sellers.length
                        ? sellers.map((npc) => {
                              const loc = npcZone(index, npc);
                              return `<div class="kb-row">${this._npcLink(npc)}${loc ? `<span class="kb-muted">${esc(this._zoneName(loc.map))}</span>` : ""}</div>`;
                          }).join("")
                        : '<div class="kb-empty">Not sold by NPCs.</div>'}
                </div>
            </section>`;
    }
}

ui.KnowledgeBaseDialog = KnowledgeBaseDialog;
ui.knowledgeBaseDialog = function () {
    return new ui.KnowledgeBaseDialog();
};
